<template>
  <TableCustomLarge
    ref="filesTable"
    class="table-custom-large"
    :fields="tableFields"
    :hasArchiveButton="store.getters.hasFileEditorAccess"
    :hasCreateButton="store.getters.hasFileCreatorAccess"
    :hasDeleteButton="store.getters.hasFileAdminAccess"
    :hasDownloadButton="store.getters.hasFileReaderAccess"
    :items="tableItems"
    :itemType="t('general__data__files')"
    :selectedIds="selectedIds"
    :resetSorting="resetSorting"
    @archiveItem="emits('archiveFile')"
    @deleteItem="emits('deleteFile')"
    @downloadItem="emits('downloadFile')"
    @onClickedRow="(item, withinCheckbox) => onRowClick(item, withinCheckbox)"
    @sortBy="handleSorting"
  >
    <template #checkbox-heading>
      <CheckBoxStandard :checked="allFilesSelected" @valueChanged="handleCheckboxAll()" />
    </template>
    <template #checkbox-data="{ item }">
      <CheckBoxStandard
        :checked="selectedFiles.find((file) => file.attachmentId === item.id) ? true : false"
        style="z-index: 2"
        @click="(event) => event.preventDefault()"
        @keydown.space="(event) => handleSpaceKey(event, item)"
      />
    </template>
    <template #placement="{ item }">
      <PulseLoader v-if="isLoading" :loading="true" color="#3F46D6" />
      <BadgeSquare
        v-else-if="item.entityId && parentNames[item.entityId] && !isLoading"
        :badgeText="parentNames[item.entityId]"
        :grey="true"
        size="small"
      />
    </template>
  </TableCustomLarge>
</template>
<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useGeneralStore } from '@/pinia'
import { useStore } from 'vuex'
import BadgeSquare from '@/design-system/atoms/BadgeSquare.vue'
import CheckBoxStandard from '@/design-system/atoms/CheckBoxStandard.vue'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import TableCustomLarge, { TableItem } from '@/design-system/molecules/TableCustomLarge.vue'
import { ActCategory, getActCategories } from '@/services/acts.service'
import { Attachment, Direction } from '@/services/attachments.service'
import { useCoreEntityStore } from '@/pinia/coreEntity'
import { getPoiById } from '@/services/pois.service'
import { getRealmId } from '@/utils/realmId'
import { getLineLayerById } from '@/services/lines.service'
import { getZoneLayerById } from '@/services/zones.service'
import * as Sentry from '@sentry/vue'

type Props = {
  fields: any[]
  items?: Attachment[]
  resetSorting?: boolean
}
const props = defineProps<Props>()
const emits = defineEmits<{
  (e: 'archiveFile'): void
  (e: 'deleteFile'): void
  (e: 'downloadFile'): void
  (e: 'selectedFiles', files: Attachment[])
  (e: 'sortBy', key: string, value: Direction): void
}>()

const { t } = useI18n()

const piniaStore = useGeneralStore()
const coreEntityStore = useCoreEntityStore()
const store = useStore()

const coreEntityTypes = ['COMPLEX', 'REAL_ESTATE', 'CONSTRUCTION', 'LEVEL', 'SPACE']

const actCategories = ref<ActCategory[] | null>(null)
const parentNames = ref<Record<string, string>>({})
const isLoading = ref(false)
const filesTable = ref()
const selectedFiles = ref<Attachment[]>([])
const tableItems = ref<TableItem[]>([])

const allFilesSelected = computed(() => {
  const filePageIds = itemsProp.value?.map((file) => file.attachmentId)
  const selectedIds = selectedFiles.value.map((file) => file.attachmentId)
  return filePageIds?.every((id) => selectedIds.includes(id))
})
const containsActs = computed<boolean>(() => {
  return itemsProp.value?.find((item) => item.actType) ? true : false
})
const itemsProp = computed(() => props.items)
const selectedIds = computed(() => {
  return selectedFiles.value.map((attachment) => attachment.attachmentId)
})
const tableFields = computed(() => {
  return props.fields
})

function getActTypeName(actTypeId: string) {
  const allTypes = actCategories.value?.flatMap((category) => category.types)
  const foundType = allTypes?.find((type) => type.id === actTypeId)
  return foundType?.name
}
function getActStatusName(actStatusId: string) {
  const allStatuses = actCategories.value?.flatMap((category) => category.statuses)
  const foundStatus = allStatuses?.find((type) => type.id === actStatusId)
  return foundStatus?.name
}
async function getEntityParents(item: TableItem): Promise<string | undefined> {
  try {
    if (!item.entityId) return undefined
    let parents
    if (coreEntityTypes.includes(item.entityType)) {
      const currentEntity = await coreEntityStore.getCoreEntity(item.entityId)
      const parentNames = await coreEntityStore.getParents(item.entityId)
      parents = [...parentNames, currentEntity]
    } else if (item.entityType === 'POI') {
      const poi = await getPoiById(item.entityId)
      if (!poi) return undefined
      const currentEntity = await coreEntityStore.getCoreEntity(poi.coreId)
      const parentNames = await coreEntityStore.getParents(poi.coreId)
      parents = [...parentNames, currentEntity, poi]
    } else if (item.entityType === 'ZONE') {
      const zone = await getZoneLayerById(item.entityId)
      if (!zone) return undefined
      const currentEntity = await coreEntityStore.getCoreEntity(zone.coreId)
      const parentNames = await coreEntityStore.getParents(zone.coreId)
      parents = [...parentNames, currentEntity, zone]
    } else if (item.entityType === 'LINE') {
      const line = await getLineLayerById(item.entityId)
      if (!line) return undefined
      const currentEntity = await coreEntityStore.getCoreEntity(line.coreId)
      const parentNames = await coreEntityStore.getParents(line.coreId)
      parents = [...parentNames, currentEntity, line]
    } else {
      // case realm
      parents = [{ name: getRealmId() }]
    }
    if (!parents) return undefined
    const parentNames = [...parents].map((parent) => parent.name).join(', ')
    return parentNames
  } catch (error) {
    Sentry.captureException(error)
    isLoading.value = false
    return undefined
  }
}
function handleCheckboxAll() {
  if (allFilesSelected.value) {
    itemsProp.value?.forEach((file) => {
      const index = selectedFiles.value.findIndex(
        (attachment) => attachment.attachmentId === file.attachmentId
      )
      selectedFiles.value.splice(index, 1)
    })
  } else {
    itemsProp.value?.forEach((file) => {
      if (!selectedFiles.value.find((attachment) => attachment.attachmentId === file.attachmentId))
        selectedFiles.value.push(file)
    })
  }
  emits('selectedFiles', selectedFiles.value)
  if (selectedFiles.value.length === 1) {
    piniaStore.setSelectedFile(selectedFiles.value[0])
  } else piniaStore.setSelectedFile(null)
}
function handleSorting(key: string, value: Direction) {
  emits('sortBy', key, value)
}
function handleSpaceKey(event: KeyboardEvent, item) {
  event.preventDefault()
  onRowClick(item, true)
}
function onRowClick(item: TableItem, withinCheckbox: boolean) {
  if (!itemsProp.value) return
  let file = itemsProp.value.find((file) => file.attachmentId === item.id)
  if (!file) return
  if (!withinCheckbox) {
    // If the row (not checkbox) was clicked, set single selected file in store
    if (
      // If there was one selected file and the file is selected again
      selectedFiles.value.length === 1 &&
      piniaStore?.selectedFile?.attachmentId === file.attachmentId
    ) {
      selectedFiles.value = []
      piniaStore.setSelectedFile(null)
    } else {
      // Remove selections and set single selected file in store
      selectedFiles.value = [file]
      piniaStore.setSelectedFile(file)
    }
    emits('selectedFiles', selectedFiles.value)
    return
  }
  if (
    // If there was one selected file and the file is selected again
    selectedFiles.value.length === 1 &&
    piniaStore?.selectedFile?.attachmentId === file.attachmentId
  ) {
    selectedFiles.value = []
    piniaStore.setSelectedFile(null)
  } else if (
    // If there were selected files and the file is not already selected
    selectedFiles.value.length >= 1 &&
    !selectedFiles.value.find((attachment) => attachment.attachmentId === file?.attachmentId)
  ) {
    selectedFiles.value.push(file)
    piniaStore.setSelectedFile(null)
  } else if (
    // If there are more than 2 selected files and the file is already selected
    selectedFiles.value.length > 2 &&
    selectedFiles.value.find((attachment) => attachment.attachmentId === file?.attachmentId)
  ) {
    const index = selectedFiles.value.findIndex(
      (attachment) => attachment.attachmentId === file?.attachmentId
    )
    selectedFiles.value.splice(index, 1)
  } else if (
    // If there were 2 selected files and the file is already selected
    selectedFiles.value.length === 2 &&
    selectedFiles.value.find((attachment) => attachment.attachmentId === file?.attachmentId)
  ) {
    const index = selectedFiles.value.findIndex(
      (attachment) => attachment.attachmentId === file?.attachmentId
    )
    selectedFiles.value.splice(index, 1)
    piniaStore.setSelectedFile(selectedFiles.value[0]) // Set selected to the remaining file
  } else {
    // If there were no selected files
    selectedFiles.value.push(file)
    piniaStore.setSelectedFile(file)
  }
  emits('selectedFiles', selectedFiles.value)
}
function resetSelections() {
  selectedFiles.value = []
  emits('selectedFiles', [])
  piniaStore.setSelectedFile(null)
}
function setSelectedFilesFromDashboard(selectedDashboardFiles: Attachment[]) {
  selectedFiles.value = selectedDashboardFiles
  emits('selectedFiles', selectedFiles.value)
}
watch(
  itemsProp,
  async (newValue) => {
    tableItems.value = []
    if (containsActs.value) {
      const actCategoriesResponse = await getActCategories()
      actCategories.value = actCategoriesResponse ?? null
    }
    if (newValue?.length) {
      const itemsArray: TableItem[] = []
      newValue.forEach((item) => {
        itemsArray.push({
          id: item.attachmentId,
          name: item.name,
          entityId: item.entityId,
          createdDate: item.createdDate,
          entityType: item.entityType,
          actType: item.actType ? getActTypeName(item.actType) : undefined,
          actStatus: item.actStatus ? getActStatusName(item.actStatus) : undefined
        })
      })
      tableItems.value = itemsArray
    }
  },
  { immediate: true }
)
watch(
  tableItems,
  async (newValue) => {
    let names = newValue.map(async (item) => {
      if (!item.entityId) return
      isLoading.value = true
      return [item.entityId, await getEntityParents(item)]
    })
    let prom = (await Promise.all(names)) as [string, string | undefined][]
    parentNames.value = Object.fromEntries(
      prom.filter((item) => item[1]).map((item) => item as [string, string])
    )
    isLoading.value = false
  },
  { immediate: true }
)
defineExpose({
  resetSelections,
  setSelectedFilesFromDashboard
})
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';

.table-custom-large {
  flex: 1 1 auto;
  height: calc((48px * 11) + 16px);
  min-height: calc((48px * 5)); // header row + 4 rows
  max-height: calc((48px * 11) + 16px); // header row + 10 rows + 16px padding
  padding: 0;
  z-index: 1;
  &__button {
    padding: 0;
    &.selected {
      background-color: variables.$color-primary;
      :deep(img) {
        filter: brightness(100);
      }
    }
    &:hover:not(.selected) {
      background-color: rgba(205, 222, 254, 0.2);
    }
  }
}
</style>
